import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { readPreload } from "../lib/preload";

/**
 * Fetches a product by slug. Returns `{ product, notFound }`.
 *   • product === null while loading
 *   • notFound === true when the API responds 404
 *   • any other error triggers a redirect back to /shop
 *
 * Extracted structurally from `pages/ProductDetail.jsx`. Behavior is
 * preserved 1-for-1 — this hook only owns the fetch + navigation side-effect.
 */
export function useProductData(slug) {
  const nav = useNavigate();
  // Prerendered snapshots embed the product so the first render has full content;
  // the fetch below still refreshes price and stock.
  const [product, setProduct] = useState(() => readPreload(`/products/${slug}`) || null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let active = true;
    setProduct((p) => (p?.slug === slug ? p : null));
    setNotFound(false);
    api
      .get(`/products/${slug}`)
      .then((r) => { if (active) setProduct(r.data); })
      .catch((err) => {
        if (!active) return;
        if (err?.response?.status === 404) setNotFound(true);
        else nav("/shop");
      });
    return () => { active = false; };
  }, [slug, nav]);

  return { product, notFound };
}
